import { FC, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';

import { ERROR } from 'constants/paths';
import { isEmpty } from 'lodash';
import { useGetPublicOnePagePlan } from 'services/businessPlan/hooks/useGetPublicOnePagePlan';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessToolStoreClear,
  getSetBusinessPlan,
  getSetBusinessPlanChapters,
} from 'store/useBusinessToolStore/selectors';

import { SkeletonLoader } from 'components/_basic/SkeletonLoader';
import { useGetInitialValues } from 'components/BusinessToolForm/utils/form';
import { OnePagePreviewContainer } from 'components/OnePageTemplates';

import { getFormValueFromChapters } from 'helpers/businessTool';
import { insertCode } from 'helpers/path';

export const OnePageSharedPage: FC = () => {
  const { id = '' } = useParams();
  const localizedInitialValues = useGetInitialValues();

  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);
  const setBusinessPlanChapters = useBusinessToolStore(getSetBusinessPlanChapters);
  const clearBusinessToolStore = useBusinessToolStore(getBusinessToolStoreClear);

  const { data, error, isLoading } = useGetPublicOnePagePlan(id);
  const lastHydratedDataRef = useRef<typeof data>();

  useEffect(() => {
    if (data && lastHydratedDataRef.current !== data) {
      setBusinessPlan(data.plan);
      setBusinessPlanChapters(getFormValueFromChapters(data.chapters, localizedInitialValues));
      lastHydratedDataRef.current = data;
    }

    return () => clearBusinessToolStore();
  }, [clearBusinessToolStore, data, localizedInitialValues, setBusinessPlan, setBusinessPlanChapters]);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!isEmpty(error)) {
    return <Navigate to={insertCode(ERROR, '404')} replace />;
  }

  return (
    <>
      <Helmet>
        <script type="text/javascript" defer>
          {`
            window.dataLayer = window.dataLayer || [];
            let userId = '';
            try {
              const userStore = localStorage.getItem('user-store');
              if (userStore) {
                const parsed = JSON.parse(userStore);
                userId = parsed?.state?.authUser?.id || '';
                dataLayer.push({ user_id: userId });
              }
            } catch (e) {
              console.error('Failed to parse user-store:', e);
            }
          `}
        </script>
        <script type="text/javascript" defer>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                      })(window,document,'script','dataLayer','GTM-PB57NZW9');`}
        </script>
        <script type="text/javascript" defer>
          {`!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '951508482576405');
            fbq('track', 'PageView');`}
        </script>
      </Helmet>
      <OnePagePreviewContainer />
    </>
  );
};
